import { ref, watch, computed } from "vue";
import { useRouter } from "vue-router";
import { throttle } from "lodash";
import { useStekManager } from "@/composables/stek-manager";
import { storeToRefs } from "pinia";
import { followLink } from "@/lib/openapi/helpers";
import { api, schemas, types } from "@/lib/openapi";
import { Artefact, type BpmnArtefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/description";
import { useOrganizationManager } from "@/composables/organization-manager";
import { defineEntityStore } from "@/stores/helpers";
import { Process } from "@/stores/processes-store";
export type { Process } from "@/stores/processes-store";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { useEntityNameManager } from "./entity-name-manager";
import { useSyncStore } from "@/stores/sync-store";
import { useEditManager } from "./edit-manager";
import {
    discardDraftsAfterSync,
    FILE_SYNC_THROTTLE_MS,
    useDraftManager,
} from "./draft-manager";
import { syncChannel } from "@/lib/broadcast-channels";

type RequiredName<T extends { name?: string }> = Omit<T, "name"> &
    Required<Pick<T, "name">>;

export type CreateTask = RequiredName<types.CreateTask>;
export type CreateGateway = RequiredName<types.CreateGateway>;
export type CreateConnection = RequiredName<types.CreateConnection>;

export type FunctionCall = CreateTask | CreateGateway | CreateConnection;

export const PROMPT_MAX_LENGTH =
    schemas.Body_start_process_process_mgt_generate_definition_start_postSchema
        .properties.prompt.maxLength;

// Uses the current route to determine what the 'process' should be and fetches
// the process description for you.
export const useProcessStore = defineEntityStore("process", () => {
    const router = useRouter();
    const { organization } = useOrganizationManager();
    const { stek } = useStekManager();

    const process = ref<Process>();
    const description = ref<Artefact<"text/markdown">>();
    const definition = ref<BpmnArtefact | undefined>();
    const syncStore = useSyncStore();
    const entityId = computed(() => process.value?.identity);
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.PROCESS,
        attribute: "description",
        artefact: description,
    });
    const definitionDraft = useDraftManager({
        entityId,
        entityType: EntityType.PROCESS,
        attribute: "process_definition",
        artefact: definition,
    });
    discardDraftsAfterSync(
        entityId,
        [descriptionDraft, definitionDraft],
        EntityType.PROCESS
    );

    const { process: processInfo } = useCurrentEntityManager();

    const isFetchingProcess = ref(false);
    const isFetchingDescription = ref(false);
    const isFetchingDefinition = ref(false);
    const controller = ref<AbortController>(new AbortController());

    const {
        isModified: isNameModified,
        reset: resetName,
        persist: persistName,
    } = useEntityNameManager(process);

    async function updateProcess(force = false) {
        const processId = processInfo.value?.identity;

        // In case the same process is selected, return early.
        if (!force && process.value && process.value.identity === processId) {
            return;
        }

        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        // If no process id is set
        if (!processId) {
            description.value = undefined;
            process.value = undefined;
            return;
        }

        isFetchingProcess.value = true;
        description.value = undefined;
        definition.value = undefined;
        process.value = undefined;

        try {
            const { response, data } =
                await api.startProcessProcessMgtGetProcessStartPost({
                    body: {
                        process_id: `${processId}`,
                    },
                    signal: controller.value.signal,
                });

            if (!response.ok) {
                if (response.status === 404) {
                    router.replace({
                        name: "not-found",
                        params: {
                            orgName: organization.value?.name,
                            stekName: stek.value?.name,
                        },
                        query: {
                            type: "process",
                            id: processId,
                        },
                    });
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            process.value = data;
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                return;
            } else {
                throw e;
            }
        } finally {
            isFetchingProcess.value = false;
        }
        // Fetch description and definition while we're still in the try block before setting isFetchingProcess=false
        if (process.value) {
            await Promise.all([
                updateDescription(processId),
                updateDefinition(processId),
            ]);
        }
    }
    watch(
        () => processInfo.value?.identity,
        () => updateProcess()
    );

    const updateDescription = async (currentProcessId?: string | undefined) => {
        if (process.value) {
            isFetchingDescription.value = true;
            try {
                const descriptionResult = await getDescriptionArtefact(
                    process.value,
                    "processDescription",
                    controller.value.signal
                );

                // Check if process has changed during the async operation
                if (
                    !process.value ||
                    process.value.identity !== currentProcessId
                ) {
                    console.debug(
                        "Process changed during description fetch, aborting description update"
                    );
                    return;
                }

                description.value = descriptionResult;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    throw e;
                }
            } finally {
                isFetchingDescription.value = false;
            }
        }
    };

    const updateDefinition = async (currentProcessId?: string | undefined) => {
        if (process.value) {
            if (process.value.process_definition) {
                isFetchingDefinition.value = true;
                try {
                    const { response, data } = await followLink<File>(
                        process.value.process_definition,
                        "get",
                        undefined,
                        controller.value.signal
                    );

                    if (!response.ok) {
                        if (response.status === 404) {
                            return;
                        }
                        throw new Error(
                            `HTTP error! status: ${response.status}`
                        );
                    }

                    // Check if process has changed during the async operation
                    if (
                        !process.value ||
                        process.value.identity !== currentProcessId
                    ) {
                        console.debug(
                            "Process changed during definition fetch, aborting definition update"
                        );
                        return;
                    }

                    definition.value = await Artefact.createBpmnArtefact({
                        name: "processDefinition",
                        identity: process.value.process_definition.identity,
                        blob: data!,
                    });

                    // Check again if process has changed during artefact creation
                    if (
                        !process.value ||
                        process.value.identity !== currentProcessId
                    ) {
                        console.debug(
                            "Process changed during artefact creation, aborting definition update"
                        );
                        return;
                    }

                    definition.value.links =
                        process.value.process_definition.links;
                } finally {
                    isFetchingDefinition.value = false;
                }
            } else {
                // Check if process has changed before setting an empty artefact
                if (
                    !process.value ||
                    process.value.identity !== currentProcessId
                ) {
                    console.debug(
                        "Process changed before creating empty artefact, aborting"
                    );
                    return;
                }

                definition.value = Artefact.createEmptyArtefact({
                    mimetype: "application/bpmn+xml",
                    identity: process.value.identity + "_definition",
                });
            }
        }
    };

    /**
     * Provide a prompt and get the functions to generate a definition back.
     */
    const generateDefinition = async (prompt: string) => {
        const { response, data } =
            await api.startProcessProcessMgtGenerateDefinitionStartPost({
                body: { prompt, process_id: process.value!.identity },
            });
        if (response.ok && data) {
            return data;
        }
    };

    const isModified = computed(() => {
        if (isFetchingProcess.value) {
            return false;
        }
        return Boolean(
            isNameModified.value ||
                description.value?.isModified ||
                definition.value?.isModified
        );
    });

    const queueSave = async () => {
        if (!isModified.value || !process.value) {
            return;
        }

        const updateContent: types.StartProcessProcessMgtUpdateProcessStartPostData =
            {
                body: {
                    process_id: process.value.identity,
                },
                url: "/process/process_mgt/update_process/start",
            };

        if (isNameModified.value) {
            updateContent.body.name = process.value.name;
        }

        if (definition.value?.isModified) {
            updateContent.body.process_definition = definition.value.blob;
            updateContent.body.process_definition_preview =
                definition.value.preview;
        }
        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (
            !updateContent.body.process_id ||
            Object.keys(updateContent.body).length === 1
        ) {
            // Only process_id is set, no need to update.
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.PROCESS,
            args: [updateContent],
        });

        if (definition.value?.isModified) {
            definition.value.persist();
        }
        if (description.value?.isModified) {
            description.value.persist();
        }
        if (isNameModified.value) {
            persistName();
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
        await definition.value?.resetContent();
        resetName();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);

    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    syncChannel.addEventListener(
        "message",
        ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.organizationId === organization.value?.identity &&
                identity.entityType === EntityType.PROCESS &&
                identity.entityId === process.value?.identity
            ) {
                updateProcess(true);
            }
        }
    );

    return {
        process,
        description,
        definition,
        generateDefinition,
        updateDefinition,
        updateProcess,
        isFetching: computed(() =>
            Boolean(
                isFetchingProcess.value ||
                    isFetchingDefinition.value ||
                    isFetchingDescription.value
            )
        ),
        isModified,
        reset,
    };
});

export const useProcessManager = () => storeToRefs(useProcessStore());
